import { AlertMessage, Link } from '@/app/components'
import { CardActionButton, Icon, Modal, Text } from '@/components'
import { EnergySystem } from '@/types/energySystem'
import { EnergySystemDemandResponseEvent } from '@/types/energySystemDemandResponseEvent'
import { getActiveEventTimeText, getUpcomingEventTimeText } from './utils'
import { useState } from 'react'
import CardActions from '@/components/CardActions/CardActions'

import toExternalLink from '@/utils/to-external-link'
import { useAppDispatch } from '@/hooks'
import { optOutOfEnergySystemDemandResponseEventAction } from '@/reducers/energySystemDemandResponseEvents'

type Props = {
  energySystem: EnergySystem
  event: EnergySystemDemandResponseEvent
  open: boolean
  onClose: () => void
  eventIsActive?: boolean
}

const DR_HELP_ARTICLE_URL =
  'https://optiwatt.zendesk.com/hc/en-us/articles/40178648687387-What-should-I-expect-during-Demand-Response-events'

export function useOptOutDecisionViewModel(props: {
  energySystem: EnergySystem
  event: EnergySystemDemandResponseEvent
  onOptedOut: () => void
  onClose: () => void
  eventIsActive?: boolean
}) {
  const dispatch = useAppDispatch()

  const optOutAlertMessage = props.eventIsActive
    ? getActiveEventTimeText(props.event)
    : getUpcomingEventTimeText(props.event)
  // todo add energy system opt out message to cms
  const eventOptOutMessage =
    'Are you sure you want to opt out of this event? Doing so may reduce your program rewards.'

  const onOptedOut = () => {
    dispatch(
      optOutOfEnergySystemDemandResponseEventAction(
        String(props.energySystem.id),
        props.event.id,
        'user_initiated_pre_event',
      ),
    )
    props.onOptedOut()
  }

  const learnMoreHref = toExternalLink(DR_HELP_ARTICLE_URL)

  return {
    optOutAlertMessage,
    eventOptOutMessage,
    onOptedOut,
    onClose: props.onClose,
    learnMoreHref,
  }
}

function OptOutDecisionView(
  props: Parameters<typeof useOptOutDecisionViewModel>[0],
) {
  const {
    optOutAlertMessage,
    eventOptOutMessage,
    learnMoreHref,
    onOptedOut,
    onClose,
  } = useOptOutDecisionViewModel(props)

  return (
    <div>
      <AlertMessage variant="info">{optOutAlertMessage}</AlertMessage>
      <Text variant="body4" className="mt-4 mb-2">
        {eventOptOutMessage}
      </Text>
      <Link to={learnMoreHref} openInNewTab>
        Learn more about DR Events
      </Link>
      <CardActions className="mt-6">
        <CardActionButton
          type="secondary"
          onClick={onOptedOut}
          id="energy-system-dr-opt-out-modal-opt-out-button"
        >
          Opt Out
        </CardActionButton>
        <CardActionButton
          type="primary"
          onClick={onClose}
          id="energy-system-dr-opt-out-modal-stay-in-event-button"
        >
          Stay in Event
        </CardActionButton>
      </CardActions>
    </div>
  )
}

function OptOutConfirmationView(props: { onClose: () => void }) {
  return (
    <div>
      <div className="flex flex-col items-center w-full justify-center">
        <Icon className="mb-4" name="Check" size={40} />
        <Text variant="subheader" className="max-w-[200px] text-center">
          You've successfully opted out of this event.
        </Text>
      </div>
      <CardActions className="mt-9">
        <CardActionButton
          type="primary"
          onClick={props.onClose}
          id="energy-system-dr-opt-out-modal-done-button"
        >
          Done
        </CardActionButton>
      </CardActions>
    </div>
  )
}

export default function EnergySystemDROptOutModal(props: Props) {
  const [hasOptedOut, setHasOptedOut] = useState(false)

  const onClose = () => {
    setHasOptedOut(false)
    props.onClose()
  }

  return (
    <Modal
      id="energy-system-dr-opt-out-modal"
      open={props.open}
      onClose={onClose}
      title={hasOptedOut ? undefined : 'Manage Event'}
    >
      {hasOptedOut ? (
        <OptOutConfirmationView onClose={onClose} />
      ) : (
        <OptOutDecisionView
          energySystem={props.energySystem}
          event={props.event}
          onOptedOut={() => setHasOptedOut(true)}
          onClose={onClose}
          eventIsActive={props.eventIsActive}
        />
      )}
    </Modal>
  )
}
